/* eslint-disable */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ValueGetterParams, ColDef } from 'ag-grid-community';
import { Map as ImmutableMap } from 'immutable';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import lightStyles from './portalStylesLight.module.css';
import darkStyles from './portalStylesDark.module.css';
import AddDialogue from './AddDialogue';
import Notes from './Notes';

type SubTab = 'Available' | 'Busy' | 'Do Not Disturb' | 'Invisible';

const subTabs: SubTab[] = ['Available', 'Busy', 'Do Not Disturb', 'Invisible'];

interface PresenceRowData {
  id: string;
  status: string;
  duration?: string | number;
  autoSwitchback?: boolean;
}

interface Props {
  presenceTables: any[];
  token: string;
  historicalQueues: ImmutableMap<string, any>;
  lightMode: boolean;
  orgId: string;
}

const MAX_LEN = 100;
const MAX_DURATION = 60 * 60 * 24; // 1 Day Max

export default function Presences({ presenceTables, token, historicalQueues, lightMode, orgId }: Props) {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('Available');
  const [rowData, setRowData] = useState<PresenceRowData[]>([]);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [prefix, setPrefix] = useState<string>('avl');
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const styles = lightMode ? lightStyles : darkStyles;

  // Initialize row data
  useEffect(() => {
    let filter = '';
    switch (activeSubTab) {
      case 'Available':
        filter = 'avl';
        break;
      case 'Busy':
        filter = 'bus';
        break;
      case 'Do Not Disturb':
        filter = 'dnd';
        break;
      default:
        filter = 'inv';
        break;
    }
    const formattedData = presenceTables[0].items
      .filter((item: any) => item.category === filter)
      .map((item: any) => ({
        id: item.id,
        status: item.status,
        duration: item.duration,
        autoSwitchback: item.autoSwitchback,
      }));
    setRowData(formattedData);
    setPrefix(filter);
  }, [activeSubTab, presenceTables]);

  // Get selected rows from the grid
  const getSelectedRows = useCallback(() => {
    if (gridRef.current) {
      return gridRef.current.api.getSelectedRows();
    }
    return [];
  }, []);

  // Add popup
  const handleAddClick = useCallback(() => {
    setShowAddDropdown((prev) => !prev);
  }, []);

  // Add
  const handleAdd = useCallback(
    async (adds: { id: string; description: string }[]) => {
      try {
        setError(null);
        const body = adds.map((add) => ({ category: prefix, group: 'default', status: add.id }));
        const response = await fetch(`${process.env.REACT_APP_API_URL}/presence`, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: {
            authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to add presence: ${response.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add presence');
        console.error('Error adding presence:', err);
      }
    },
    [prefix, token],
  );

  // Delete
  const handleDelete = useCallback(async () => {
    try {
      setError(null);
      const selectedRows = getSelectedRows();
      if (selectedRows.length === 0) {
        setError('No rows selected for deletion');
        return;
      }

      const body = selectedRows.map((row) => ({
        group: 'default',
        id: row.id,
      }));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/presence`, {
        method: 'DELETE',
        body: JSON.stringify(body),
        headers: {
          authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete presence: ${response.statusText}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete presence');
      console.error('Error deleting presence:', err);
    }
  }, [getSelectedRows, token]);

  // Validate duration input
  const validateDuration = (value: any): number | null => {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numValue = Number(value);
    if (isNaN(numValue) || numValue <= 0 || numValue >= MAX_DURATION || !Number.isInteger(numValue)) {
      return null;
    }

    return numValue;
  };

  // Edit value
  const onCellValueChanged = useCallback(
    async (event: any) => {
      try {
        setError(null);

        // Handle duration field specifically
        if (event.colDef.field === 'duration') {
          const validatedDuration = validateDuration(event.newValue);
          if (validatedDuration === null && event.newValue !== null && event.newValue !== '') {
            setError('Duration must be a positive integer');
            return;
          }

          // Set autoSwitchback to true if duration is being added and it wasn't set before
          if (validatedDuration !== null && (!event.oldValue || event.oldValue <= 0)) {
            event.data.autoSwitchback = true;
          }

          // Update the data with validated duration
          event.data.duration = validatedDuration;
        }

        const queues = Array.from(historicalQueues.values());
        const usedInQueues =
          prefix === 'dnd'
            ? queues.filter((queue) => queue.acw && queue.acw.label === event.oldValue).map((queue) => queue.id)
            : [];
        const body = {
          category: prefix,
          duration: event.data.duration,
          group: 'default',
          id: event.data.id,
          queues: usedInQueues,
          status: event.data.status,
          autoSwitchback: event.data.autoSwitchback,
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}/presence/default`, {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: {
            authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to update presence: ${response.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update presence');
        console.error('Error updating presence:', err);
      }
    },
    [prefix, historicalQueues, token],
  );

  // Edit order
  const onRowDragEnd = useCallback(
    async (event: any) => {
      try {
        setError(null);
        if (!gridRef.current) return;

        // Get all row nodes in current order
        const rowNodes: any[] = [];
        gridRef.current.api.forEachNode((node) => rowNodes.push(node));

        // Extract the IDs in the new order
        const body = rowNodes.map((node) => node.data.id);
        const response = await fetch(`${process.env.REACT_APP_API_URL}/presence/order/default`, {
          method: 'PUT',
          body: JSON.stringify(body),
          headers: {
            authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to update presence order: ${response.statusText}`);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to update presence order');
        console.error('Error updating presence order:', err);
      }
    },
    [token],
  );

  // Custom checkbox renderer component
  const CustomCheckboxRenderer = useCallback(
    (params: any) => {
      const dur = Number(params.data?.duration);

      // Don't render checkbox if duration is not positive
      if (!dur || dur <= 0 || isNaN(dur)) {
        return null;
      }

      const isChecked = params.data?.autoSwitchback !== false;

      const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
          setError(null);
          const newValue = event.target.checked;
          const oldValue = params.data.autoSwitchback;

          // Update locally first
          params.data.autoSwitchback = newValue;

          // Update the grid
          params.api.refreshCells({ force: true });

          // Call the cell value changed handler
          await onCellValueChanged({
            colDef: { field: 'autoSwitchback' },
            data: params.data,
            oldValue,
            newValue,
          });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to update auto switchback');
          console.error('Error updating auto switchback:', err);
        }
      };

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <input type="checkbox" checked={isChecked} onChange={handleCheckboxChange} />
        </div>
      );
    },
    [onCellValueChanged],
  );

  const columnDefs = useMemo<ColDef<PresenceRowData>[]>(() => {
    const cols: any[] = [
      {
        headerName: '',
        field: 'drag',
        suppressMenu: true,
        suppressSizeToFit: true,
        rowDrag: true,
        width: 50,
      },
      {
        headerName: '',
        field: 'selected',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMenu: true,
        width: 50,
        suppressSizeToFit: true,
      },
      {
        headerName: 'Status',
        field: 'status',
        editable: true,
        flex: 1,
        valueSetter: (params: { newValue: any; data: { status: any } }) => {
          const v = (params.newValue ?? '').toString();
          if (v.length > MAX_LEN || v.length < 1) {
            return false;
          }
          params.data.status = v;
          return true;
        },
      },
      {
        headerName: 'Duration (Seconds)',
        field: 'duration',
        editable: true,
        flex: 1,
        headerTooltip:
          'The number of seconds this status will last for before the user is either moved into Available or Late.',
        cellEditor: 'agNumberCellEditor',
        cellEditorParams: {
          min: 1,
          max: MAX_DURATION,
          step: 5,
        },
        valueParser: (params: any) => {
          return validateDuration(params.newValue);
        },
      },
    ];
    cols.push({
      headerName: 'Auto Switchback',
      field: 'autoSwitchback',
      editable: false,
      flex: 1,
      headerTooltip:
        "If Auto Switchback is checked (true) then a user in this status will be moved into Available when the timer elapses. If this is unchecked then the user will instead be moved into a 'late' state until they manually move themselves.",
      valueGetter: (params: ValueGetterParams<PresenceRowData>) => {
        const dur = Number(params.data?.duration);
        if (dur <= 0) {
          return null;
        }
        return params.data?.autoSwitchback;
      },
      cellRenderer: CustomCheckboxRenderer,
    });
    return cols;
  }, [CustomCheckboxRenderer]);

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: true,
      resizable: true,
      minWidth: 50,
    }),
    [],
  );

  return (
    <div className={styles.gridContainer}>
      {error && (
        <div
          className={styles.errorMessage}
          style={{
            color: 'red',
            padding: '8px',
            margin: '8px 0',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            borderRadius: '4px',
            border: '1px solid red',
          }}
        >
          {error}
        </div>
      )}
      <div className={styles.navbar}>
        <div className={styles.tabButtons}>
          {subTabs.map((subTab) => (
            <button
              key={subTab}
              type="button"
              onClick={() => setActiveSubTab(subTab)}
              className={`${styles.subTabButton} ${activeSubTab === subTab ? styles.subTabButtonActive : ''}`}
            >
              {subTab}
            </button>
          ))}
        </div>
        <div className={styles.actionButtonsContainer}>
          <div className={styles.actionButtonsWrapper}>
            <button ref={addButtonRef} type="button" onClick={handleAddClick} className={styles.actionButton}>
              Add
            </button>
            {showAddDropdown && (
              <AddDialogue
                lightMode={lightMode}
                onAdd={handleAdd}
                onClose={() => setShowAddDropdown(false)}
                format='"&lt;Status&gt;"'
                example='"Do Not Disturb"'
                params={1}
                validate={(codes) => {
                  for (const code of codes) {
                    if (code.id.length > MAX_LEN) {
                      return `Status "${code.id}" must be ${MAX_LEN} characters or less.`;
                    }
                  }
                  return null;
                }}
              />
            )}
            <button type="button" onClick={handleDelete} className={styles.actionButton}>
              Delete
            </button>
          </div>
        </div>
      </div>

      <div
        className={`ag-theme-alpine${!lightMode ? '-dark' : ''}  ${styles.agThemeAlpineOverrides} ${
          styles.gridWrapper
        }`}
        style={{ flex: 3 }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          animateRows
          suppressCellFocus
          rowSelection="multiple"
          suppressRowClickSelection
          suppressDragLeaveHidesColumns
          headerHeight={40}
          rowHeight={40}
          rowDragManaged
          onRowDragEnd={onRowDragEnd}
          suppressMoveWhenRowDragging
          tooltipShowDelay={500}
        />
      </div>
      <Notes
        lightMode={lightMode}
        notes={`
            This page controls what presences your agents can choose.
            Each presence is associated with a category found on the navbar above.
            Note that when users set Busy or Invisible, it will not 
            take effect in the RingCentral App.
            Duration must be a positive integer. When a duration is added,
            auto switchback will be enabled by default. Setting a duration to 
            zero will remove the timer for that status.
          `}
        // documents={[
        //   { name: 'Supporting Document', uri: 'https://google.ca' },
        //   { name: 'Instruction Manual', uri: 'https://yahoo.ca' },
        //   { name: 'Demo', uri: 'https://bing.com' },
        // ]}
      />
    </div>
  );
}
